import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Loader2,
  MessageCircle,
  Send,
  UserRound,
  X,
} from "lucide-react";

import "./SoporteChat.css";

const API_BASE = "/api/soporte";

function SoporteChat() {
  const [abierto, setAbierto] = useState(false);

  // Protocolo del chatbot
  const [pasos, setPasos] = useState([]);
  const [pasoActual, setPasoActual] = useState(null);

  // Opción seleccionada del protocolo
  const [opcionSeleccionada, setOpcionSeleccionada] =
    useState(null);

  // Opciones configuradas para aparecer después de una opción
  const [opcionesMostradas, setOpcionesMostradas] =
    useState([]);

  // Mensajes visuales del chatbot
  const [mensajes, setMensajes] = useState([]);

  // Solicitud de soporte
  const [solicitud, setSolicitud] = useState(null);

  // Mostrar formulario para representante
  const [mostrarFormulario, setMostrarFormulario] =
    useState(false);

  // Datos del cliente
  const [datosCliente, setDatosCliente] = useState({
    nombre_cliente: "",
    correo_cliente: "",
    telefono_cliente: "",
    motivo: "",
  });

  // Mensaje para representante
  const [mensaje, setMensaje] = useState("");

  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  const mensajesRef = useRef(null);

  /*
   * =========================================================
   * CARGAR PROTOCOLO ACTIVO
   * =========================================================
   */

  const cargarProtocolo = async () => {
    try {
      setCargando(true);
      setError("");

      const response = await fetch(
        `${API_BASE}/protocolo/activo`
      );

      const data = await response.json();

      console.log("RESPUESTA COMPLETA AL SELECCIONAR:", data);
      console.log(
        "OPCIONES MOSTRADAS RECIBIDAS:",
        data?.opciones_mostradas
      );

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            "No se pudo cargar el protocolo."
        );
      }

      const lista = data?.pasos || [];

      setPasos(lista);

      if (lista.length === 0) {
        setPasoActual(null);

        setMensajes([
          {
            id: `bot-${Date.now()}`,
            remitente_tipo: "bot",
            mensaje:
              "Hola. ¿En qué podemos ayudarte?",
          },
        ]);

        return;
      }

      const primerPaso = lista[0];

      setPasoActual(primerPaso);

      setMensajes([
        {
          id: `bot-${Date.now()}`,
          remitente_tipo: "bot",
          mensaje: primerPaso.mensaje,
        },
      ]);
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "No se pudo cargar el asistente."
      );

      setMensajes([
        {
          id: `bot-error-${Date.now()}`,
          remitente_tipo: "bot",
          mensaje:
            "Hola 👋 Estamos para ayudarte. En este momento no pudimos cargar nuestro asistente.",
        },
      ]);
    } finally {
      setCargando(false);
    }
  };

  /*
   * =========================================================
   * ABRIR CHAT
   * =========================================================
   */

  const abrirChat = () => {
    setAbierto(true);

    if (pasos.length === 0 && !cargando) {
      cargarProtocolo();
    }
  };

  /*
   * =========================================================
   * SELECCIONAR OPCIÓN
   * =========================================================
   */

  const seleccionarOpcion = async (opcion) => {
    console.log("CLIC EN OPCIÓN:", opcion);
    console.log("PASO ACTUAL AL HACER CLIC:", pasoActual);
    console.log("CARGANDO AL HACER CLIC:", cargando);

    if ((!pasoActual && !opcion?.paso_id) || cargando) {
      console.log("SELECCIÓN BLOQUEADA");
      return;
    }

    setError("");

    /*
     * IMPORTANTE:
     * Guardamos el objeto completo de la opción.
     *
     * Esto permite utilizarlo posteriormente cuando
     * el cliente complete el formulario para hablar
     * con un representante.
     */
    setOpcionSeleccionada(opcion);

    // Mostrar selección del cliente
    setMensajes((actuales) => [
      ...actuales,
      {
        id: `cliente-opcion-${Date.now()}`,
        remitente_tipo: "cliente",
        mensaje: opcion.texto,
      },
    ]);

    /*
     * =====================================================
     * TRANSFERENCIA A REPRESENTANTE
     * =====================================================
     */

    if (opcion.transferir_representante) {
      setMostrarFormulario(true);

      setDatosCliente((actuales) => ({
        ...actuales,
        motivo:
          actuales.motivo ||
          opcion.texto,
      }));

      setMensajes((actuales) => [
        ...actuales,
        {
          id: `bot-form-${Date.now()}`,
          remitente_tipo: "bot",
          mensaje:
            "Claro. Para conectarte con un representante necesitamos algunos datos.",
        },
      ]);

      /*
       * NO limpiamos opcionSeleccionada aquí.
       *
       * Se necesita después para llamar:
       *
       * POST /api/soporte/protocolo/seleccionar
       *
       * con paso_id + opcion_id.
       */

      return;
    }

    /*
     * =====================================================
     * OPCIÓN NORMAL
     * =====================================================
     */

    setCargando(true);

    try {
      const response = await fetch(
        `${API_BASE}/protocolo/seleccionar`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paso_id: opcion.paso_id || pasoActual.id,
            opcion_id: opcion.id,
          }),
        }
      );

      const data = await response.json();

      console.log(
        "RESPUESTA REAL DE SELECCIONAR:",
        data
      );

      console.log(
        "OPCIONES MOSTRADAS DE SELECCIONAR:",
        data?.opciones_mostradas
      );

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            "No se pudo procesar la opción."
        );
      }

      /*
       * El backend puede indicar transferencia.
       */

      if (data.transferir_representante) {
        setMostrarFormulario(true);

        setDatosCliente((actuales) => ({
          ...actuales,
          motivo:
            actuales.motivo ||
            opcion.texto,
        }));

        setMensajes((actuales) => [
          ...actuales,
          {
            id: `bot-transfer-${Date.now()}`,
            remitente_tipo: "bot",
            mensaje:
              "Claro. Para conectarte con un representante necesitamos algunos datos.",
          },
        ]);

        return;
      }

      const siguienteId =
        data?.siguiente_paso_id;

      /*
       * MENSAJES CONFIGURADOS PARA LA OPCIÓN
       *
       * Estos mensajes son administrables desde el panel.
       * Una opción puede tener uno, dos, cinco o más mensajes.
       */

      const mensajesOpcion = Array.isArray(data?.mensajes)
        ? data.mensajes
        : [];

      const opcionesMostradasRespuesta =
        Array.isArray(data?.opciones_mostradas)
          ? data.opciones_mostradas
          : [];

      setOpcionesMostradas(
        opcionesMostradasRespuesta
      );

      if (mensajesOpcion.length > 0) {
        setMensajes((actuales) => [
          ...actuales,
          ...mensajesOpcion.map((item, index) => ({
            id: `bot-opcion-${opcion.id}-${item.id || index}-${Date.now()}`,
            remitente_tipo: "bot",
            mensaje: item.mensaje,
          })),
        ]);
      }

      /*
       * No hay siguiente paso.
       *
       * Si el administrador configuró mensajes para esta opción,
       * esos mensajes son la respuesta y NO mostramos el mensaje
       * genérico de "Hemos terminado...".
       */

      if (!siguienteId) {
        if (mensajesOpcion.length === 0) {
          setMensajes((actuales) => [
            ...actuales,
            {
              id: `bot-final-${Date.now()}`,
              remitente_tipo: "bot",
              mensaje:
                "Hemos terminado esta parte del proceso. ¿Hay algo más en lo que podamos ayudarte?",
            },
          ]);
        }

        setPasoActual(null);

        return;
      }

      /*
       * Buscar siguiente paso dentro del protocolo
       * que ya cargamos.
       */

      const siguientePaso =
        pasos.find(
          (paso) =>
            paso.id === siguienteId
        );

      console.log(
        "SIGUIENTE PASO ENCONTRADO:",
        siguientePaso
      );

      console.log(
        "OPCIONES DEL SIGUIENTE PASO:",
        siguientePaso?.opciones
      );

      if (!siguientePaso) {
        throw new Error(
          "No se encontró el siguiente paso del protocolo."
        );
      }

      setPasoActual(siguientePaso);

      setMensajes((actuales) => [
        ...actuales,
        {
          id: `bot-${siguientePaso.id}-${Date.now()}`,
          remitente_tipo: "bot",
          mensaje:
            siguientePaso.mensaje,
        },
      ]);
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "No se pudo procesar tu selección."
      );

      setMensajes((actuales) => [
        ...actuales,
        {
          id: `bot-error-${Date.now()}`,
          remitente_tipo: "bot",
          mensaje:
            "No pudimos procesar esa opción. Intenta nuevamente.",
        },
      ]);
    } finally {
      setCargando(false);

      /*
       * Solamente limpiamos la opción si NO estamos
       * esperando el formulario del representante.
       */
      if (!mostrarFormulario) {
        setOpcionSeleccionada(null);
      }
    }
  };

  /*
   * =========================================================
   * ENVIAR SOLICITUD CON REPRESENTANTE
   * =========================================================
   */

  const enviarSolicitudRepresentante = async (e) => {
    e.preventDefault();

    if (!pasoActual) {
      return;
    }

    /*
     * La opción debe existir.
     */

    if (!opcionSeleccionada) {
      setError(
        "No se pudo identificar la opción seleccionada."
      );

      return;
    }

    const nombre =
      datosCliente.nombre_cliente.trim();

    const correo =
      datosCliente.correo_cliente
        .trim()
        .toLowerCase();

    const telefono =
      datosCliente.telefono_cliente.trim();

    const motivo =
      datosCliente.motivo.trim();

    /*
     * Validación
     */

    if (!nombre) {
      setError("Escribe tu nombre.");
      return;
    }

    if (!correo || !correo.includes("@")) {
      setError(
        "Escribe un correo electrónico válido."
      );
      return;
    }

    await enviarSolicitudConOpcion(
      opcionSeleccionada,
      {
        nombre,
        correo,
        telefono,
        motivo,
      }
    );
  };

  /*
   * =========================================================
   * CREAR SOLICITUD
   * =========================================================
   */

  const enviarSolicitudConOpcion = async (
    opcion,
    datos
  ) => {
    setCargando(true);
    setError("");

    try {
      const response = await fetch(
        `${API_BASE}/protocolo/seleccionar`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            paso_id: pasoActual.id,

            opcion_id: opcion.id,

            nombre_cliente:
              datos.nombre,

            correo_cliente:
              datos.correo,

            telefono_cliente:
              datos.telefono || null,

            motivo:
              datos.motivo ||
              opcion.texto,
          }),
        }
      );

      const data = await response.json();

      console.log(
        "RESPUESTA REAL DE SELECCIONAR:",
        data
      );

      console.log(
        "OPCIONES MOSTRADAS DE SELECCIONAR:",
        data?.opciones_mostradas
      );

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            "No se pudo crear la solicitud."
        );
      }

      /*
       * El backend debe confirmar transferencia.
       */

      if (
        !data.transferir_representante ||
        !data.solicitud
      ) {
        throw new Error(
          "El servidor no confirmó la transferencia al representante."
        );
      }

      /*
       * Guardamos solicitud.
       */

      setSolicitud(data.solicitud);

      /*
       * Ocultamos formulario.
       */

      setMostrarFormulario(false);

      /*
       * Mensaje del chatbot.
       */

      setMensajes((actuales) => [
        ...actuales,
        {
          id: `bot-solicitud-${Date.now()}`,
          remitente_tipo: "bot",
          mensaje:
            data.message ||
            "Tu solicitud fue enviada correctamente. Estamos buscando un representante disponible.",
        },
      ]);

      /*
       * Ya no necesitamos la opción.
       */

      setOpcionSeleccionada(null);
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "No pudimos enviar tu solicitud."
      );
    } finally {
      setCargando(false);
    }
  };

  /*
   * =========================================================
   * CONSULTAR MENSAJES
   * =========================================================
   */

  const consultarMensajes = async () => {
    if (!solicitud?.id || !solicitud?.token_acceso) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/chat/solicitudes/${solicitud.id}/mensajes`,
        {
          headers: {
            "X-Solicitud-Token": solicitud.token_acceso,
          },
        }
      );

      if (!response.ok) {
        return;
      }

      const data = await response.json();

      const mensajesServidor =
        data?.mensajes || [];

      setMensajes((actuales) => {
        /*
         * Mensajes locales que pertenecen al flujo
         * del chatbot.
         */

        const mensajesLocales =
          actuales.filter(
            (item) =>
              typeof item.id === "string" &&
              (
                item.id.startsWith("bot-") ||
                item.id.startsWith(
                  "cliente-opcion-"
                )
              )
          );

        /*
         * Evitar duplicados de mensajes persistidos.
         */

        const idsServidor =
          new Set(
            mensajesServidor.map(
              (item) => item.id
            )
          );

        const localesFiltrados =
          mensajesLocales.filter(
            (item) =>
              !idsServidor.has(item.id)
          );

        return [
          ...localesFiltrados,
          ...mensajesServidor,
        ];
      });

      /*
       * Actualizar estado.
       */

      if (data.estado) {
        setSolicitud((actual) => {
          if (!actual) {
            return actual;
          }

          return {
            ...actual,
            estado: data.estado,
          };
        });
      }
    } catch (err) {
      console.error(
        "Error consultando mensajes:",
        err
      );
    }
  };

  /*
   * =========================================================
   * POLLING DEL CHAT
   * =========================================================
   */

  useEffect(() => {
    if (!solicitud?.id) {
      return;
    }

    consultarMensajes();

    const intervalo = setInterval(
      consultarMensajes,
      3000
    );

    return () =>
      clearInterval(intervalo);
  }, [solicitud?.id]);

  /*
   * =========================================================
   * SCROLL AUTOMÁTICO
   * =========================================================
   */

  useEffect(() => {
    if (!mensajesRef.current) {
      return;
    }

    mensajesRef.current.scrollTop =
      mensajesRef.current.scrollHeight;
  }, [mensajes]);

  /*
   * =========================================================
   * ENVIAR MENSAJE
   * =========================================================
   */

  const enviarMensaje = async (e) => {
    e.preventDefault();

    const texto = mensaje.trim();

    if (
      !texto ||
      !solicitud?.id ||
      solicitud.estado !== "activa"
    ) {
      return;
    }

    setMensaje("");
    setError("");

    try {
      const response = await fetch(
        `${API_BASE}/chat/solicitudes/${solicitud.id}/mensajes`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Solicitud-Token": solicitud.token_acceso || "",
          },
          body: JSON.stringify({
            mensaje: texto,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data?.detail ||
            "No se pudo enviar el mensaje."
        );
      }

      setMensajes((actuales) => [
        ...actuales,
        data.mensaje,
      ]);
    } catch (err) {
      console.error(err);

      setError(
        err?.message ||
          "No se pudo enviar el mensaje."
      );

      setMensaje(texto);
    }
  };

  /*
   * =========================================================
   * REINICIAR CHAT
   * =========================================================
   */

  const reiniciarChat = () => {
    setPasos([]);
    setPasoActual(null);
    setMensajes([]);
    setSolicitud(null);
    setMostrarFormulario(false);
    setOpcionSeleccionada(null);
    setMensaje("");
    setError("");

    setDatosCliente({
      nombre_cliente: "",
      correo_cliente: "",
      telefono_cliente: "",
      motivo: "",
    });

    cargarProtocolo();
  };

  /*
   * =========================================================
   * VOLVER AL INICIO
   * =========================================================
   */

  const volverAlInicio = () => {
    if (!pasos.length) {
      return;
    }

    const primerPaso = pasos[0];

    setPasoActual(primerPaso);
    setMostrarFormulario(false);
    setOpcionSeleccionada(null);
    setError("");

    setMensajes((actuales) => [
      ...actuales,
      {
        id: `bot-inicio-${Date.now()}`,
        remitente_tipo: "bot",
        mensaje:
          primerPaso.mensaje,
      },
    ]);
  };

  /*
   * =========================================================
   * ESTADOS
   * =========================================================
   */

  const opciones =
    pasoActual?.opciones || [];

  const opcionesVisibles = [
    ...opciones,
    ...opcionesMostradas,
  ].filter(
    (opcion, index, lista) =>
      lista.findIndex(
        (item) => item.id === opcion.id
      ) === index
  );

  const conversacionActiva =
    solicitud?.estado === "activa";

  const solicitudPendiente =
    solicitud?.estado === "pendiente";

  /*
   * =========================================================
   * RENDER
   * =========================================================
   */

  return (
    <>
      {abierto && (
        <div className="soporte-chat-window">

          {/* HEADER */}

          <div className="soporte-chat-header">

            <div className="soporte-chat-title">

              <div className="soporte-chat-avatar">
                RM
              </div>

              <div>
                <strong>
                  Soporte RedMobility
                </strong>

                <small>
                  {conversacionActiva
                    ? "Representante conectado"
                    : solicitudPendiente
                    ? "Esperando representante"
                    : "Estamos para ayudarte"}
                </small>
              </div>

            </div>

            <button
              type="button"
              onClick={() =>
                setAbierto(false)
              }
              aria-label="Cerrar chat"
            >
              <X size={20} />
            </button>

          </div>


          {/* MENSAJES */}

          <div
            className="soporte-chat-messages"
            ref={mensajesRef}
          >

            {mensajes.map(
              (item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className={
                    item.remitente_tipo ===
                    "cliente"
                      ? "chat-message client"
                      : item.remitente_tipo ===
                        "representante"
                      ? "chat-message representative"
                      : "chat-message bot"
                  }
                >

                  {item.remitente_tipo !==
                    "cliente" && (

                    <div className="chat-message-icon">

                      {item.remitente_tipo ===
                      "representante" ? (
                        <UserRound
                          size={14}
                        />
                      ) : (
                        <MessageCircle
                          size={14}
                        />
                      )}

                    </div>

                  )}

                  <div className="chat-message-bubble">
                    {item.mensaje}
                  </div>

                </div>
              )
            )}


            {/* CARGANDO */}

            {cargando && (
              <div className="chat-message bot">

                <div className="chat-message-icon">
                  <Loader2
                    size={14}
                    className="soporte-chat-loading-icon"
                  />
                </div>

                <div className="chat-message-bubble">
                  Procesando...
                </div>

              </div>
            )}


            {/* OPCIONES DEL PROTOCOLO */}

            {!solicitud &&
              !mostrarFormulario &&
              (pasoActual || opcionesMostradas.length > 0) &&
              opcionesVisibles.length > 0 && (

                <div className="soporte-chat-options">

                  {opcionesVisibles.map(
                    (opcion) => (

                      <button
                        key={opcion.id}
                        type="button"
                        className={
                          opcion.transferir_representante
                            ? "soporte-chat-option representante"
                            : "soporte-chat-option"
                        }
                        onClick={() =>
                          seleccionarOpcion(
                            opcion
                          )
                        }
                        disabled={cargando}
                      >

                        <span>
                          {opcion.texto}
                        </span>

                        <ArrowRight
                          size={16}
                        />

                      </button>

                    )
                  )}

                </div>

              )}


            {/* FORMULARIO PARA REPRESENTANTE */}

            {!solicitud &&
              mostrarFormulario && (

                <form
                  className="soporte-chat-form"
                  onSubmit={
                    enviarSolicitudRepresentante
                  }
                >

                  <div className="soporte-chat-form-title">
                    <strong>
                      Hablar con un representante
                    </strong>

                    <span>
                      Completa tus datos para continuar.
                    </span>
                  </div>

                  <input
                    type="text"
                    placeholder="Tu nombre"
                    value={
                      datosCliente.nombre_cliente
                    }
                    onChange={(e) =>
                      setDatosCliente(
                        (actual) => ({
                          ...actual,
                          nombre_cliente:
                            e.target.value,
                        })
                      )
                    }
                    disabled={cargando}
                  />

                  <input
                    type="email"
                    placeholder="Tu correo electrónico"
                    value={
                      datosCliente.correo_cliente
                    }
                    onChange={(e) =>
                      setDatosCliente(
                        (actual) => ({
                          ...actual,
                          correo_cliente:
                            e.target.value,
                        })
                      )
                    }
                    disabled={cargando}
                  />

                  <input
                    type="tel"
                    placeholder="Teléfono (opcional)"
                    value={
                      datosCliente.telefono_cliente
                    }
                    onChange={(e) =>
                      setDatosCliente(
                        (actual) => ({
                          ...actual,
                          telefono_cliente:
                            e.target.value,
                        })
                      )
                    }
                    disabled={cargando}
                  />

                  <textarea
                    placeholder="¿En qué podemos ayudarte?"
                    value={
                      datosCliente.motivo
                    }
                    onChange={(e) =>
                      setDatosCliente(
                        (actual) => ({
                          ...actual,
                          motivo:
                            e.target.value,
                        })
                      )
                    }
                    disabled={cargando}
                  />

                  <button
                    type="submit"
                    disabled={cargando}
                  >
                    {cargando
                      ? (
                        <>
                          <Loader2
                            size={16}
                            className="soporte-chat-loading-icon"
                          />

                          Enviando...
                        </>
                      )
                      : (
                        <>
                          <UserRound
                            size={16}
                          />

                          Solicitar representante
                        </>
                      )}
                  </button>

                </form>

              )}


            {/* SOLICITUD PENDIENTE */}

            {solicitudPendiente && (

              <div className="soporte-chat-status">

                <div className="soporte-chat-status-icon">
                  <CheckCircle2
                    size={30}
                  />
                </div>

                <strong>
                  Solicitud enviada
                </strong>

                <small>
                  Estamos buscando un
                  representante disponible.
                </small>

              </div>

            )}


            {/* CONVERSACIÓN CERRADA */}

            {solicitud?.estado ===
              "cerrada" && (

              <div className="soporte-chat-status">

                <div className="soporte-chat-status-icon">
                  <CheckCircle2
                    size={30}
                  />
                </div>

                <strong>
                  Conversación finalizada
                </strong>

                <small>
                  Gracias por contactar con
                  RedMobility.
                </small>

                <button
                  type="button"
                  onClick={reiniciarChat}
                >
                  Nueva conversación
                </button>

              </div>

            )}


            {/* ERROR */}

            {error && (

              <div className="soporte-chat-error">
                {error}
              </div>

            )}

          </div>


          {/* INPUT DEL CHAT */}

          {conversacionActiva && (

            <form
              className="soporte-chat-input"
              onSubmit={enviarMensaje}
            >

              <input
                type="text"
                value={mensaje}
                onChange={(e) =>
                  setMensaje(
                    e.target.value
                  )
                }
                placeholder="Escribe un mensaje..."
              />

              <button
                type="submit"
                disabled={!mensaje.trim()}
                aria-label="Enviar mensaje"
              >
                <Send size={18} />
              </button>

            </form>

          )}


          {/* VOLVER */}

          {!solicitud &&
            !mostrarFormulario &&
            pasoActual &&
            pasos.length > 0 &&
            pasoActual.id !==
              pasos[0]?.id && (

              <div className="soporte-chat-back">

                <button
                  type="button"
                  onClick={
                    volverAlInicio
                  }
                >
                  <ArrowLeft
                    size={14}
                  />

                  Volver al inicio
                </button>

              </div>

            )}

        </div>
      )}


      {/* BOTÓN FLOTANTE */}

      <button
        type="button"
        className="soporte-chat-floating"
        onClick={() => {

          if (abierto) {
            setAbierto(false);
          } else {
            abrirChat();
          }

        }}
        aria-label={
          abierto
            ? "Cerrar soporte"
            : "Abrir soporte"
        }
      >

        {abierto ? (
          <X size={25} />
        ) : (
          <MessageCircle size={25} />
        )}

      </button>
    </>
  );
}

export default SoporteChat;
